/**
 * 京都府の背景ツアー「京都めぐり」コンテンツ。
 *
 * 軸は一日の時間。早朝の平等院から、夜の千本鳥居へ。
 * 京都市内は4か所にとどめ、宇治市・和束町・宮津市・南丹市・伊根町・舞鶴市に6か所を配している。
 *
 * 伏見稲荷の千本鳥居について。
 *  山口（元乃隅神社）で赤い鳥居の列をすでに使っている。
 *  元乃隅は屋外の斜面で、鳥居の間隔が空き、海と空が見える明るい昼の絵。
 *  千本鳥居は柱がほぼ接するほど密で、外の景色も空も見えない薄暗いトンネル、夜の絵。
 *  英語記述に「開けた斜面に間隔をあけて並ぶ列ではなく、閉じた暗い回廊である」と明示して分けている。
 *  鳥居が主題なので、鳥居の否定文は入れていない。
 *
 * 南禅寺水路閣について。
 *  「国宝」と書かない。京都市上下水道局のページは文化審議会の答申の段階で、
 *  「官報告示を経て国宝・重要文化財となる予定」という書き方。
 *  書けるのは明治21年の建造と、平成8年に疏水の12施設が国の史跡に指定されたこと。
 *
 * 天橋立について。
 *  「日本三景」「特別名勝」はいずれも国・府の公式で確認できなかった。書かない。
 *  書けるのは、幅20〜170メートル・全長約3.6キロ・松およそ6700本という観光協会の数値。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 堂内の仏像・障壁画は描かせない。平等院の阿弥陀如来坐像も雲中供養菩薩も壁画も。建物の外観だけ。
 *  - 宇治を「日本初の都市の重要文化的景観」と書かない（市公式のみの記載）。
 *  - 宇治の紹介で源氏物語に触れない。小説・映画・アニメ作品と結びつけない。
 *  - 拝観料・拝観時間・特別公開・ライトアップの期間は書かない。
 */

import type { StageTour } from '../types';

export const kyotoSceneryTour: StageTour = {
  scenes: [
    {
      id: 'byodoin',
      name: '平等院 鳳凰堂',
      area: '宇治市・早朝',
      image: '/assets/scenery/kyoto/scenes/01_byodoin.webp',
      hotspots: [
        {
          id: 'byodoin-1052',
          x: 0.46,
          y: 0.42,
          title: '1052年に寺となった',
          body: '藤原道長が譲り受けた別業を、その子頼通が永承7年（1052年）に寺に改めたものです。鳳凰堂は屋根の上に鳳凰を戴き、堂内の仏像や壁画とともに国宝に指定されています。庭園は史跡・名勝です。',
          source: 'https://www.pref.kyoto.jp/isan/byoudou.html',
          mapQuery: '平等院 宇治市',
        },
        {
          id: 'byodoin-17shisan',
          x: 0.74,
          y: 0.34,
          title: '世界遺産17資産のひとつ',
          body: '「古都京都の文化財」として世界文化遺産に登録されている資産は17。京都市の14、宇治市の2（平等院・宇治上神社）、大津市の1（延暦寺）で構成されます。',
          source: 'https://www.pref.kyoto.jp/isan/index.html',
          mapQuery: '宇治 世界遺産',
        },
        {
          id: 'byodoin-keikan',
          x: 0.22,
          y: 0.5,
          title: '街ごと守られる景観',
          body: '「宇治の文化的景観」は平成21年2月12日に国の重要文化的景観に選定されました。面積228.5ヘクタール。宇治川や宇治橋、宇治と白川の茶畑や茶に関わる建物まで含み、13分類95の構成要素が特定されています。',
          source: 'https://www.city.uji.kyoto.jp/site/bunkazai/7330.html',
          mapQuery: '宇治橋 宇治川',
        },
      ],
    },
    {
      id: 'wazuka',
      name: '和束の茶畑',
      area: '相楽郡和束町・初夏の午前',
      image: '/assets/scenery/kyoto/scenes/02_wazuka.webp',
      hotspots: [
        {
          id: 'wazuka-utsukushii',
          x: 0.46,
          y: 0.44,
          title: '日本で最も美しい村',
          body: '平成25年10月4日、和束町は「日本で最も美しい村」連合に52番目の会員として加盟しました。京都府内では伊根町に次いで2例目。茶畑と瓦屋根の集落景観、鎌倉時代から受け継ぐ茶文化を守るまちづくりが評価されました。',
          source: 'https://www.town.wazuka.lg.jp/kanko_tokusan/keikanshisan/2236.html',
          mapQuery: '和束町 茶畑',
        },
        {
          id: 'wazuka-hifuku',
          x: 0.74,
          y: 0.34,
          title: '黒い覆いをかける茶畑',
          body: '被覆栽培は新芽の生育中に茶園を遮光資材で覆う方法で、400年以上前に始まりました。当初は春の霜よけでしたが、やがて高品質茶のための技術になりました。よしずとわらを使う伝統的な本ず被覆もあります。',
          source: 'https://www.pref.kyoto.jp/chaken/mame_shade.html',
          mapQuery: '和束 石寺の茶畑',
        },
        {
          id: 'wazuka-nihonisan',
          x: 0.22,
          y: 0.5,
          title: '茶の道は山城に続く',
          body: '「日本茶800年の歴史散歩」として日本遺産に認定されており、宇治市・和束町・宇治田原町・木津川市・八幡市・京田辺市・井手町・笠置町などが含まれます。茶畑・茶問屋の町並み・製茶工場・寺社が構成要素です。',
          source: 'https://www.city.uji.kyoto.jp/site/uji-cha/3902.html',
          mapQuery: '和束町 原山 茶畑',
        },
      ],
    },
    {
      id: 'arashiyama-chikurin',
      name: '嵐山 竹林の小径',
      area: '京都市右京区・午前',
      image: '/assets/scenery/kyoto/scenes/03_arashiyama_chikurin.webp',
      hotspots: [
        {
          id: 'chikurin-400m',
          x: 0.46,
          y: 0.44,
          title: 'およそ400メートルの竹の道',
          body: '竹林の小径はおよそ400メートル。野宮神社から天龍寺の北門を経て、大河内山荘の方へ続いています。嵐電「嵐山」駅から徒歩約10分、JR「嵯峨嵐山」駅から徒歩約13分の距離にあります。',
          source: 'https://ja.kyoto.travel/tourism/single01.php?category_id=8&tourism_id=2683',
          mapQuery: '竹林の小径 嵯峨野',
        },
        {
          id: 'chikurin-kumori',
          x: 0.74,
          y: 0.34,
          title: '曇りの日は昼でも暗い',
          body: '空を覆うほど竹が整然と立ち並ぶため、晴れた日は葉の間から光が差し込み、曇った日は日中でも薄暗くなります。同じ道でも天気によって見え方がまったく変わります。平安時代には貴族の別荘地だった一帯です。',
          source: 'https://ja.kyoto.travel/tourism/single01.php?category_id=8&tourism_id=2683',
          mapQuery: '嵯峨嵐山 竹林',
        },
        {
          id: 'chikurin-tenryuji',
          x: 0.22,
          y: 0.5,
          title: '嵐山と亀山を背景に',
          body: '嵐山の天龍寺は、暦応2年（1339年）に足利尊氏が創建した禅寺で、京都五山の一位として栄えました。方丈裏の庭園は史跡・特別名勝で、嵐山と亀山を背景とした池泉回遊式庭園です。',
          source: 'https://www.pref.kyoto.jp/isan/tenryuji.html',
          mapQuery: '天龍寺 嵐山',
        },
      ],
    },
    {
      id: 'amanohashidate',
      name: '天橋立',
      area: '宮津市・真昼',
      image: '/assets/scenery/kyoto/scenes/04_amanohashidate.webp',
      hotspots: [
        {
          id: 'amano-36km',
          x: 0.46,
          y: 0.44,
          title: '3.6キロの松の帯',
          body: '天橋立は幅およそ20メートルから170メートル、全長およそ3.6キロメートルの砂の細長い地形で、およそ6700本の松が生えています。自然が数千年かけてつくり出したものとされています。',
          source: 'https://www.amanohashidate.jp/spot/amanohashidate/',
          mapQuery: '天橋立 宮津市',
        },
        {
          id: 'amano-50pun',
          x: 0.74,
          y: 0.34,
          title: '歩いて片道50分',
          body: '松並木の中の道は、歩いて片道およそ50分、自転車ならおよそ20分です。国定公園に指定されているため、車で通行することはできません。',
          source: 'https://www.amanohashidate.jp/spot/amanohashidate/',
          mapQuery: '天橋立 松並木',
        },
        {
          id: 'amano-hozen',
          x: 0.22,
          y: 0.5,
          title: '世界遺産をめざす砂の道',
          body: '京都府と宮津市・伊根町・与謝野町が、天橋立の世界文化遺産登録をめざす取り組みを進めています。宮津市は「祖先から深い感謝の念を抱き、守り育ててきた美しい天橋立を、子ども達に残しましょう」と呼びかけ、周辺も含めた景観の保全を進めています。',
          source: 'https://www.city.miyazu.kyoto.jp/soshiki/17/2060.html',
          mapQuery: '傘松公園 天橋立',
        },
      ],
    },
    {
      id: 'miyama',
      name: '美山かやぶきの里',
      area: '南丹市美山町・冬',
      image: '/assets/scenery/kyoto/scenes/05_miyama.webp',
      hotspots: [
        {
          id: 'miyama-39mune',
          x: 0.46,
          y: 0.42,
          title: '50戸のうち39棟が茅葺き',
          body: '集落は50戸で、そのうち39棟がかやぶき屋根です。江戸時代から明治時代、およそ220年から150年前に建てられたものが残っています。南丹市美山町の北集落にあたります。',
          source: 'https://kayabukinosato.jp/about/',
          mapQuery: '美山かやぶきの里 南丹市',
        },
        {
          id: 'miyama-sanson',
          x: 0.74,
          y: 0.34,
          title: '種別は「山村集落」',
          body: '重要伝統的建造物群保存地区としての種別は山村集落です。京都府内の七つの保存地区のうち、この種別は美山町北だけで、ほかは社家町・門前町・茶屋町・漁村・製織町にあたります。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '美山町北 集落',
        },
        {
          id: 'miyama-1275ha',
          x: 0.22,
          y: 0.5,
          title: '面積127.5ヘクタール',
          body: '南丹市美山町北は平成5年12月8日に重要伝統的建造物群保存地区に選定されました。面積は127.5ヘクタールで、府内では伊根浦の310.2ヘクタールに次いで広い地区です。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: 'かやぶきの里 北村',
        },
      ],
    },
    {
      id: 'kiyomizudera',
      name: '清水寺 本堂の舞台',
      area: '京都市東山区・晩秋の午後',
      image: '/assets/scenery/kyoto/scenes/06_kiyomizudera.webp',
      hotspots: [
        {
          id: 'kiyomizu-778',
          x: 0.46,
          y: 0.38,
          title: '滝の清らかさが名になった',
          body: '清水寺の開創は778年。音羽山で清らかな湧き水を見つけた僧が、のちに坂上田村麻呂とともに寺を興したと伝わります。十一面千手観音を本尊とし、清水という寺の名は、その滝の清らかさに由来します。',
          source: 'https://www.kiyomizudera.or.jp/history.php',
          mapQuery: '清水寺 東山区',
        },
        {
          id: 'kiyomizu-kugi',
          x: 0.74,
          y: 0.52,
          title: '釘を使わない木組み',
          body: '現在の伽藍の多くは1633年の再建です。本堂から張り出した舞台は崖の上およそ13メートルの高さにあり、釘を使わない日本古来の伝統工法による木組みで支えられています。',
          source: 'https://www.kiyomizudera.or.jp/history.php',
          mapQuery: '清水の舞台',
        },
        {
          id: 'kiyomizu-juyo15',
          x: 0.22,
          y: 0.48,
          title: '境内に重文がおよそ15',
          body: '本堂は国宝に指定されています。境内には仁王門、西門、三重塔など、およそ15の重要文化財が集まっています。平安時代から参詣の地として親しまれてきました。',
          source: 'https://www.pref.kyoto.jp/isan/kiyomizu.html',
          mapQuery: '清水寺 仁王門',
        },
      ],
    },
    {
      id: 'suirokaku',
      name: '南禅寺 水路閣',
      area: '京都市左京区・夕方前',
      image: '/assets/scenery/kyoto/scenes/07_suirokaku.webp',
      hotspots: [
        {
          id: 'suirokaku-5nen',
          x: 0.46,
          y: 0.44,
          title: '五年でつくった水の道',
          body: '琵琶湖疏水は明治18年（1885年）6月に着工し、明治23年（1890年）4月に完成しました。東京への遷都で衰えた京都に活気を取り戻すため、当時の知事・北垣国道が始めた事業で、発電・工場・舟運に水を使いました。',
          source: 'https://www.city.kyoto.lg.jp/suido/page/0000007153.html',
          mapQuery: '南禅寺 水路閣',
        },
        {
          id: 'suirokaku-tanabe',
          x: 0.74,
          y: 0.34,
          title: '工事を率いたのは新卒の技師',
          body: '工事の技術を率いたのは、工部大学校を出たばかりの田邉朔郎でした。第一隧道は長さ2436メートルで、日本で初めて竪坑工法が使われました。工事はほとんど人力で、れんがや木材も地元でまかないました。',
          source: 'https://www.city.kyoto.lg.jp/suido/page/0000007153.html',
          mapQuery: '琵琶湖疏水 蹴上',
        },
        {
          id: 'suirokaku-1888',
          x: 0.22,
          y: 0.5,
          title: '明治21年に架けられた',
          body: '南禅寺水路閣は明治21年（1888年）の建造です。平成8年（1996年）には、インクラインなど疏水の12の施設が国の史跡に指定されています。',
          source: 'https://www.city.kyoto.lg.jp/suido/page/0000341284.html',
          mapQuery: '水路閣 左京区',
        },
      ],
    },
    {
      id: 'ine-funaya',
      name: '伊根の舟屋',
      area: '与謝郡伊根町・夕方',
      image: '/assets/scenery/kyoto/scenes/08_ine_funaya.webp',
      hotspots: [
        {
          id: 'ine-garage',
          x: 0.46,
          y: 0.46,
          title: '一階は船のガレージ',
          body: '舟屋は漁師の作業施設で、船を海から引き上げて風雨や虫から守るために建てられました。一階が船の格納庫、二階は網の干し場や漁具置き場として使われてきました。伊根湾の沿岸におよそ230軒が並びます。',
          source: 'https://www.ine-kankou.jp/spot/funaya',
          mapQuery: '伊根の舟屋 伊根町',
        },
        {
          id: 'ine-sumu',
          x: 0.74,
          y: 0.36,
          title: '住むのは道の向かい',
          body: '舟屋は作業用の建物なので、住民の大半は道を挟んだ主屋に住んでいます。舟屋が子ども夫婦の住まいや老後の離れになることもあります。船を使わない一階は魚をさばいたり洗濯物を干したりする場になっています。',
          source: 'https://www.ine-kankou.jp/spot/funaya',
          mapQuery: '伊根湾 舟屋群',
        },
        {
          id: 'ine-judenken',
          x: 0.22,
          y: 0.5,
          title: '府内最大の保存地区',
          body: '伊根町伊根浦は平成17年7月22日に重要伝統的建造物群保存地区に選定されました。種別は漁村で、面積は310.2ヘクタール。京都府内の七地区のうち最も広い地区です。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '伊根浦 重要伝統的建造物群保存地区',
        },
      ],
    },
    {
      id: 'maizuru-akarenga',
      name: '舞鶴赤れんがパーク',
      area: '舞鶴市・日没後',
      image: '/assets/scenery/kyoto/scenes/09_maizuru_akarenga.webp',
      hotspots: [
        {
          id: 'maizuru-1901',
          x: 0.46,
          y: 0.44,
          title: '明治から大正の倉庫群',
          body: '赤れんが倉庫は明治34年（1901年）から大正10年（1921年）にかけて次々に建てられました。最も古い棟は明治35年の建造です。現在はれんが造2階建11棟と鉄骨れんが造1棟の計12棟が残ります。',
          source: 'https://akarenga-park.com/about',
          mapQuery: '舞鶴赤れんがパーク',
        },
        {
          id: 'maizuru-8mune',
          x: 0.74,
          y: 0.34,
          title: '12棟のうち8棟が重文',
          body: '12棟の赤れんが倉庫のうち8棟が、平成20年（2008年）に重要文化財に指定されています。平成24年（2012年）に舞鶴赤れんがパークとしてグランドオープンしました。',
          source: 'https://akarenga-park.com/about',
          mapQuery: '舞鶴 赤れんが倉庫群',
        },
        {
          id: 'maizuru-chinjufu',
          x: 0.22,
          y: 0.5,
          title: '四つの鎮守府の一つ',
          body: '平成28年（2016年）4月、「鎮守府 横須賀・呉・佐世保・舞鶴」として日本遺産に認定されました。舞鶴は旧海軍の鎮守府が置かれた四つの町の一つです。',
          source: 'https://akarenga-park.com/about',
          mapQuery: '舞鶴港 赤れんが',
        },
      ],
    },
    {
      id: 'fushimi-inari',
      name: '伏見稲荷大社 千本鳥居',
      area: '京都市伏見区・夜',
      image: '/assets/scenery/kyoto/scenes/10_fushimi_inari.webp',
      hotspots: [
        {
          id: 'inari-711',
          x: 0.46,
          y: 0.4,
          title: '御鎮座は711年',
          body: '稲荷大神が稲荷山に鎮座したのは奈良時代の711年、二月初午の日と伝わります。2011年に御鎮座1300年を迎えました。全国におよそ3万社あるといわれる稲荷神社の総本宮です。',
          source: 'https://www.inari.jp/about/',
          mapQuery: '伏見稲荷大社 伏見区',
        },
        {
          id: 'inari-toru',
          x: 0.74,
          y: 0.5,
          title: '祈りと感謝の鳥居',
          body: '千本鳥居は奥社奉拝所へ向かう参道です。祈りと感謝の気持ちを鳥居の奉納で表す信仰は江戸時代に興り、いまの千本鳥居をかたちづくりました。稲荷信仰で朱は、明るい希望や生命の力を表す色とされています。',
          source: 'https://inari.jp/trip/map01/',
          mapQuery: '千本鳥居 奥社奉拝所',
        },
        {
          id: 'inari-romon',
          x: 0.22,
          y: 0.48,
          title: '楼門は1589年の造営',
          body: '楼門は天正17年（1589年）に豊臣秀吉が造営したと伝わり、昭和48年の解体修理で同じ年の墨書が見つかりました。本殿は明応8年（1499年）の再興で、重要文化財に指定されています。',
          source: 'https://inari.jp/trip/map01/',
          mapQuery: '伏見稲荷大社 楼門',
        },
      ],
    },
  ],
};
